import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import AuthApiService from "services/authApiService";
import ApiService from "services/apiService";
import type { GetInvitationResult } from "features/auth/auth.types";

export const loginUser = createAsyncThunk(
    "auth/loginUser",
    async (payload: {
        email: string;
        password: string;
        client_id: string | number;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await AuthApiService({
                method: "post",
                endpoint: "/client/v1/login",
                data: payload,
                apiHeaders: payload.apiHeaders,
            });

            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to login user"
                );
            } else {
                throw new Error(
                    error?.error || error?.message || "Failed to login user"
                );
            }
        }
    }
);

export const sendOtp = createAsyncThunk(
    "auth/sendOtp",
    async (payload: {
        email?: string;
        phone?: string;
        otp_type: "email" | "sms";
        login_hash: string;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await AuthApiService({
                method: "post",
                endpoint: "/client/v1/send-otp",
                data: payload,
                apiHeaders: payload.apiHeaders,
            });
            if (response.status !== "OK") {
                throw new Error(response?.data?.message || "Failed to send otp");
            }

            return response.md5_string;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error || error.response?.data?.message || "Failed to send otp"
                );
            } else {
                throw new Error(error?.error || error?.message || "Failed to send otp");
            }
        }
    }
);

export const checkOtp = createAsyncThunk(
    "auth/checkOtp",
    async (payload: {
        otp_token: string;
        otp_type: string;
        md5_string: string;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await AuthApiService({
                method: "post",
                endpoint: "/client/v1/check-otp",
                data: payload,
                apiHeaders: payload.apiHeaders,
            });
            if (response.status !== "OK") {
                throw new Error(response?.data?.message || "Failed to check otp");
            }
            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error || error.response?.data?.message || "Failed to check otp"
                );
            } else {
                throw new Error(
                    error?.error || error?.message || "Failed to check otp"
                );
            }
        }
    }
);

export const forgetPassword = createAsyncThunk(
    "auth/forgetPassword",
    async (payload: {
        email: string;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await AuthApiService({
                method: "post",
                endpoint: "/client/v1/forget-password",
                data: payload,
                apiHeaders: payload.apiHeaders,
            });
            if (response.status !== "OK") {
                throw new Error(
                    response?.data?.message || "Failed to send forget password request"
                );
            }
            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to send forget password request"
                );
            } else {
                throw new Error(
                    error?.error ||
                    error?.message ||
                    "Failed to send forget password request"
                );
            }
        }
    }
);

export const resetPassword = createAsyncThunk(
    "auth/resetPassword",
    async (payload: {
        reset_password: string;
        new_password: string;
        new_password_confirmation: string;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await AuthApiService({
                method: "post",
                endpoint: "/client/v1/reset-password",
                data: payload,
                apiHeaders: payload.apiHeaders,
            });
            if (response.status !== "OK") {
                throw new Error(
                    response?.data?.message || "Failed to send reset password request"
                );
            }
            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to send reset password request"
                );
            } else {
                throw new Error(
                    error?.error ||
                    error?.message ||
                    "Failed to send reset password request"
                );
            }
        }
    }
);

export const registerUser = createAsyncThunk(
    "auth/register",
    async (payload: {
        fname: string;
        lname: string;
        phone: string;
        country_code: string;
        email: string;
        password: string;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await AuthApiService({
                method: "post",
                endpoint: "/client/v1/register",
                data: payload,
                apiHeaders: payload.apiHeaders,
            });
            if (response.status !== "OK") {
                throw new Error(response?.data?.message || "Failed to register user");
            }
            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to register user"
                );
            } else {
                throw new Error(
                    error?.error || error?.message || "Failed to register user"
                );
            }
        }
    }
);

export const getAgreement = createAsyncThunk(
    "auth/getAgreement",
    async ({
               token,
               apiHeaders,
           }: {
        token: string;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await AuthApiService({
                method: "get",
                endpoint: "/client/v1/get-agreement",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                apiHeaders,
            });
            if (response.status !== "OK") {
                throw new Error(response?.data?.message || "Failed to register user");
            }
            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to register user"
                );
            } else {
                throw new Error(
                    error?.error || error?.message || "Failed to register user"
                );
            }
        }
    }
);

export const acceptAgreement = createAsyncThunk(
    "auth/acceptAgreement",
    async ({
               id,
               token,
               apiHeaders,
           }: {
        id: string;
        token: string;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await AuthApiService({
                method: "post",
                endpoint: "/client/v1/accept-agreement",
                data: { id },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                apiHeaders,
            });
            if (response.status !== "OK") {
                throw new Error(response?.data?.message || "Failed to register user");
            }
            return response;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to register user"
                );
            } else {
                throw new Error(
                    error?.error || error?.message || "Failed to register user"
                );
            }
        }
    }
);

export const getInvitation = createAsyncThunk(
    "auth/getInvitation",
    async (payload: {
        key: string;
        token: string | undefined;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }): Promise<GetInvitationResult> => {
        const { key, token, apiHeaders } = payload;
        const { clientId, locale } = apiHeaders;

        // Use direct axios so we can inspect all response shapes:
        // result.status === "OK"                         → invitation data (flow: "ok")
        // result.status === "FAILED" + data.register     → redirect to register (flow: "register")
        // result.status === "FAILED" + data.login        → redirect to login (flow: "login")
        // result.status === "FAILED" (no redirect data)  → expired/invalid → throw
        const url = process.env.backEndPoint + `/client/v1/get_invitation/${key}`;
        const headers: Record<string, string> = {
            clientId: clientId || "",
            lang: locale || "",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        try {
            const response = await axios({ method: "get", url, headers });
            const result = response.data.response;

            if (result.status === "OK") {
                return { flow: "ok", ...result.data } as GetInvitationResult;
            } else if (result.status === "FAILED") {
                if (result.data?.register === true) {
                    return { flow: "register", url: result.data.url, email: result.data.email };
                } else if (result.data?.login === true) {
                    return { flow: "login", url: result.data.url, email: result.data.email };
                } else {
                    throw new Error(result.message || "Invitation not found");
                }
            } else {
                throw new Error(result.message || "Failed to get invitation");
            }
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                const status = error.response?.status;
                const responseData = error.response?.data?.response;

                // 401 means the backend is signalling a redirect (register or login).
                // Mirror the old apiService.ts logic (lines 67-74).
                if (status === 401 && responseData?.data?.url) {
                    if (typeof localStorage !== "undefined" && responseData.data.email) {
                        localStorage.setItem("email", responseData.data.email);
                    }
                    if (responseData.data.login === true) {
                        return { flow: "login", url: responseData.data.url, email: responseData.data.email };
                    }
                    return { flow: "register", url: responseData.data.url, email: responseData.data.email || "" } as GetInvitationResult;
                }

                throw new Error(
                    responseData?.message ||
                    error.message ||
                    "Failed to get invitation"
                );
            }
            throw error;
        }
    }
);

export const respondInvitation = createAsyncThunk(
    "auth/respondInvitation",
    async ({
               key,
               status,
               client_id,
               token,
               apiHeaders,
           }: {
        key: string;
        status: string;
        client_id: string;
        token: string;
        apiHeaders: {
            token?: string;
            clientSlug?: string;
            locale?: string;
            clientId?: string;
        };
    }) => {
        try {
            const response = await ApiService({
                method: "post",
                endpoint: "/client/v1/respond_invitation",
                data: { key, status, client_id },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                apiHeaders,
            });
            if (response.status !== "OK") {
                throw new Error(
                    response?.data?.message || "Failed to respond invitation"
                );
            }
            return response;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to respond invitation"
                );
            } else {
                throw new Error(
                    error?.error || error?.message || "Failed to respond invitation"
                );
            }
        }
    }
);

export const logoutUser = createAsyncThunk(
    "auth/logoutUser",
    async (
        params: {
            is_sso_login?: boolean;
            id_token?: string | null;
            locale?: string;
            clientSlug?: string;
        } = {},
        { dispatch, getState }
    ) => {
        try {
            // Get state before clearing to check SSO user status
            const state = getState() as any;
            const authData = state?.auth?.data;

            // Use provided params or fallback to state
            const isSsoLogin = params?.is_sso_login ?? authData?.is_sso_login ?? false;
            const idToken = params?.id_token ?? authData?.id_token ?? null;
            const locale = params?.locale ?? state?.config?.locale ?? "en";
            const clientSlug = params?.clientSlug ?? state?.config?.slug ?? "";

            // Handle SSO logout if user is SSO user and has id_token
            if (isSsoLogin && idToken && typeof window !== "undefined") {
                try {
                    // Clear local state first before SSO logout redirect
                    dispatch({ type: "auth/resetState" });

                    // Clear all localStorage items related to authentication
                    localStorage.removeItem("token");
                    localStorage.removeItem("md5");
                    localStorage.removeItem("otp-value");
                    localStorage.removeItem("otp-type");
                    localStorage.removeItem("email");
                    localStorage.removeItem("persist:root");

                    // Clear sessionStorage
                    sessionStorage.clear();

                    // Purge redux-persist persisted state
                    const { persistor } = await import("store");
                    await persistor.purge();

                    // Build SSO logout URL
                    const ssoLogoutBaseUrl = process.env.NEXT_PUBLIC_SSO_LOGOUT_URL ?? "https://sso.riyadhgreen.sa/adfs/oauth2/logout";
                    const ssoRedirectBase = process.env.NEXT_PUBLIC_SSO_REDIRECT_BASE_URL ?? "https://gallery.riyadhgreen.sa";
                    const loginRedirectUrl = encodeURIComponent(
                        `${ssoRedirectBase}/${locale}/${clientSlug}/login`
                    );
                    const ssoLogoutUrl = `${ssoLogoutBaseUrl}?id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${loginRedirectUrl}`;

                    // Redirect to SSO logout - this will handle the SSO session logout
                    window.location.href = ssoLogoutUrl;

                    // Return early - the SSO logout will redirect back to login page
                    return { success: true, ssoLogout: true };
                } catch (ssoError) {
                    console.error("SSO logout error:", ssoError);
                    // Fall through to normal logout if SSO logout fails
                }
            }

            // Normal logout flow
            // Reset Redux state
            dispatch({ type: "auth/resetState" });

            // Clear all localStorage items related to authentication
            if (typeof window !== "undefined") {
                localStorage.removeItem("token");
                localStorage.removeItem("md5");
                localStorage.removeItem("otp-value");
                localStorage.removeItem("otp-type");
                localStorage.removeItem("email");
                // Clear redux-persist storage
                localStorage.removeItem("persist:root");
            }

            // Clear sessionStorage
            if (typeof window !== "undefined") {
                sessionStorage.clear();
            }

            // Purge redux-persist persisted state
            const { persistor } = await import("store");
            await persistor.purge();

            return { success: true, ssoLogout: false };
        } catch (error: any) {
            // Even if there's an error, we still want to clear what we can
            if (typeof window !== "undefined") {
                localStorage.removeItem("token");
                localStorage.removeItem("md5");
                localStorage.removeItem("otp-value");
                localStorage.removeItem("otp-type");
                localStorage.removeItem("email");
                localStorage.removeItem("persist:root");
                sessionStorage.clear();
            }
            const { persistor } = await import("store");
            await persistor.purge();
            throw error;
        }
    }
);
